import isArray from "lodash/isArray";
import isBoolean from "lodash/isBoolean";
import { OptionsProps } from "PFComponents/select_v2";
import { useBooleanToString } from "PFCore/hooks/use_boolean_to_string";
import { Filter, Id, OptionsFilter } from "PFTypes";
import { FilterV2 } from "PFTypes/filters_v2";
import { Option } from "PFTypes/options";
import { ReactNode, useCallback } from "react";

import { ProfileItem } from "../profile_item";

export type OptionOriginal = { id: Id; text: string; displayElement?: ReactNode };
type FilterOption = Option<OptionOriginal>;

type UseFiltersOptions = {
  filter: Filter;
};

type UseFiltersOptionsReturn = {
  optionsProps: OptionsProps<OptionOriginal>;
  renderDisplayValue: (option: FilterOption) => ReactNode;
};

export const useFiltersOptions = ({ filter }: UseFiltersOptions): UseFiltersOptionsReturn => {
  const booleanToString = useBooleanToString();
  const formatOptions = useCallback(
    (originalOptions) =>
      originalOptions.map((option) => {
        const displayElement = isBoolean(option.text) ? (
          booleanToString(option.text)
        ) : option.displayElement ||
          filter.entity_name === "profile" ||
          (filter as unknown as FilterV2).entityName === "profile" ? (
          <ProfileItem
            profile={option}
            displayAvatar={false}
            displayJobTitle={filter.name === "workforce_member_id"}
            displayEmail
            displaySuspendedDate
            clipOverflow={false}
          />
        ) : undefined;

        return {
          id: option.id,
          value: option.text,
          original: option,
          displayElement
        };
      }),
    [filter.name]
  );

  const fetchOptions = useCallback(
    async ({ ids, searchTerm }) => {
      if (!filter.query) {
        return { entries: [], meta: { total: 0 } };
      }

      const options = await filter.query({ value: ids, term: searchTerm });
      if (isArray(options)) {
        return { entries: formatOptions(options), meta: {} };
      }
      return { entries: formatOptions(options.entries), meta: options.meta };
    },
    [filter, formatOptions]
  );

  const optionsProps = (filter as OptionsFilter).options
    ? { options: formatOptions((filter as OptionsFilter).options) }
    : { fetchOptions };

  const renderDisplayValue = (option) => {
    if (
      (filter.entity_name === "profile" || (filter as unknown as FilterV2).entityName === "profile") &&
      !!option?.original
    ) {
      return (
        <ProfileItem
          profile={option?.original as any}
          displayAvatar={false}
          displayJobTitle={filter.name === "workforce_member_id"}
          displayEmail
        />
      );
    }
    return option?.displayElement || option?.value;
  };

  return {
    optionsProps,
    renderDisplayValue
  };
};
